import { skipToken, useQuery } from "@apollo/client/react";
import { Box, Grid } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { InlineError, Typography } from "@pmag/ui-components";
import { CountChip } from "@src/components/countChip/CountChip";
import { Routes } from "@src/context/router/RouterContext";
import { type GQLCategorySelectionQuery, type GQLCategorySelectionQueryVariables } from "@src/graphql.generated";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { useRouter } from "@src/hooks/useRouter";
import { categorySelectionQuery } from "@src/pages/landingPage/components/categorySelection/CategorySelection.gql";
import { CategorySelectionSkeleton } from "@src/pages/landingPage/components/categorySelection/CategorySelectionSkeleton";
import { type FunctionComponent } from "react";
import { FormattedMessage } from "react-intl";

import { Card, CardContent } from "./CategorySelection.styles";

export const dataTestId = {
    container: "sparePartsFinder.landingPage.categorySelection.container",
    loadingSkeleton: "sparePartsFinder.landingPage.brandSelection.loadingSkeleton",
    inlineError: "sparePartsFinder.landingPage.categorySelection.inlineError",
    card: "sparePartsFinder.landingPage.categorySelection.card",
};

export const CategorySelection: FunctionComponent = () => {
    const { setRoute } = useRouter();
    const { setBreadcrumbs, breadcrumbs, brandId } = useGlobalState();

    const { data, loading, error, refetch } = useQuery<GQLCategorySelectionQuery, GQLCategorySelectionQueryVariables>(
        categorySelectionQuery,
        brandId ? { variables: { brandId } } : skipToken,
    );

    if (error) return <InlineError onRetry={refetch} data-testid={dataTestId.inlineError} />;

    return (
        <Box data-testid={dataTestId.container} mb={8}>
            <Box mb={3}>
                <Typography variant="label-02">
                    <FormattedMessage id="sparePartsFinder.landingPage.categorySelection.title" defaultMessage="or select your category" />
                </Typography>
            </Box>

            {data ? (
                <Grid
                    container
                    columns={{
                        lg: 12,
                        md: 12,
                        sm: 8,
                        xl: 12,
                        xs: 4,
                    }}
                    spacing={3}
                >
                    {data.models.nodes.map((item, index) => (
                        <Grid item xs={8} sm={4} md={6} lg={4} xl={3} key={`category-${index}`}>
                            <Card
                                onClick={() => {
                                    setBreadcrumbs(breadcrumbs.concat([{ categoryId: item.id, description: item.description ?? "" }]));
                                    setRoute(Routes.VisualSelector);
                                }}
                                data-testid={dataTestId.card}
                            >
                                <CardContent ml={6}>
                                    <Box mb={2}>
                                        <Typography variant="heading-06">{item.description}</Typography>
                                    </Box>

                                    <CountChip count={item.children.length} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                loading && <CategorySelectionSkeleton data-testid={dataTestId.loadingSkeleton} />
            )}
        </Box>
    );
};
